import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { navigate } from '../router.js';
import { LoadError, Loading } from '../ui.js';

/**
 * The project archive (owner 2026-11-02): every project, the Hub's own (delivered ones and those outside the list's
 * months too) and Monday's history since 2021, searched on the server 50 at a time. Nothing is read until someone opens
 * it, so the everyday list costs what it did. Feedback on an old project: find it here, then open it (a Hub project) or
 * open it in the Hub again (a Monday one).
 */

export interface ArchiveRow {
  kind: 'hub' | 'monday';
  id: string;
  name: string;
  clientId: string | null;
  clientName: string | null;
  month: string | null;
  status: string | null;
  cancelled: boolean;
  fromMonday: boolean;
  driveUrl: string | null;
  ziflowUrl: string | null;
  hubProjectId: string | null;
}
interface ArchivePage {
  total: number;
  offset: number;
  pageSize: number;
  rows: ArchiveRow[];
  canReopen: boolean;
}

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_SV = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const TX = texts(
  {
    intro: 'Every project, delivered ones and Monday’s history since 2021 too. Feedback on an old project? Search for it and open it again.',
    search: 'Search project or client',
    count: (n: number) => `${n.toLocaleString('en')} ${n === 1 ? 'project' : 'projects'}`,
    hits: (n: number, q: string) => `${n.toLocaleString('en')} ${n === 1 ? 'match' : 'matches'} for “${q}”`,
    none: 'No project matches the search.',
    project: 'Project',
    month: 'Month',
    status: 'Status',
    source: 'Source',
    hub: 'Hub',
    monday: 'Monday',
    more: 'Show more',
    reopen: 'Open in the Hub',
    reopenHint: (name: string) => `Make a Hub project of “${name}” to handle new feedback`,
    opened: 'Opened in the Hub',
    drive: 'Drive',
    ziflow: 'Ziflow',
    noMonth: 'No month',
    months: MONTHS_EN,
    status_: {
      planned: 'Planned',
      pre_production: 'Pre-production',
      in_production: 'In production',
      in_review: 'In review',
      delivering: 'Delivering',
      delivered: 'Delivered',
      closed: 'Closed'
    } as Record<string, string>,
    cancelled: 'Cancelled'
  },
  {
    intro: 'Alla projekt, även levererade och Mondays historik sedan 2021. Fått feedback på ett gammalt projekt? Sök fram det och öppna det igen.',
    search: 'Sök projekt eller kund',
    count: (n: number) => `${n.toLocaleString('sv')} projekt`,
    hits: (n: number, q: string) => `${n.toLocaleString('sv')} ${n === 1 ? 'träff' : 'träffar'} på ”${q}”`,
    none: 'Inget projekt matchar sökningen.',
    project: 'Projekt',
    month: 'Månad',
    status: 'Status',
    source: 'Källa',
    hub: 'Hubben',
    monday: 'Monday',
    more: 'Visa fler',
    reopen: 'Öppna i Hubben',
    reopenHint: (name: string) => `Gör ett Hubb-projekt av ”${name}” för att ta hand om ny feedback`,
    opened: 'Öppnat i Hubben',
    drive: 'Drive',
    ziflow: 'Ziflow',
    noMonth: 'Ingen månad',
    months: MONTHS_SV,
    status_: {
      planned: 'Planerat',
      pre_production: 'Förproduktion',
      in_production: 'Produktion',
      in_review: 'Granskning',
      delivering: 'Levereras',
      delivered: 'Levererat',
      closed: 'Avslutat'
    } as Record<string, string>,
    cancelled: 'Avbrutet'
  }
);

const path = (q: string, offset: number) => {
  const s = new URLSearchParams();
  if (q.trim()) s.set('q', q.trim());
  if (offset) s.set('offset', String(offset));
  const qs = s.toString();
  return `/api/projects/archive${qs ? `?${qs}` : ''}`;
};

export function ProjectArchive(p: { api: Api; onReopen: (row: ArchiveRow) => void }) {
  const t = TX[useLang()];
  const [q, setQ] = useState('');
  const [page, setPage] = useState<ArchivePage | null>(null);
  const [rows, setRows] = useState<ArchiveRow[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // only the latest search's answer is shown, however the answers arrive
  const seq = useRef(0);
  const load = (query: string, offset: number) => {
    const n = ++seq.current;
    setBusy(true);
    p.api.get<ArchivePage>(path(query, offset)).then(
      (r) => {
        if (n !== seq.current) return;
        setPage(r);
        setRows((cur) => (offset ? [...cur, ...r.rows] : r.rows));
        setBusy(false);
      },
      (e) => {
        if (n !== seq.current) return;
        setErr(e);
        setBusy(false);
      }
    );
  };
  // a search waits for a pause in typing, so one query goes to the server, not one per letter
  useEffect(() => {
    const h = setTimeout(() => load(q, 0), q ? 300 : 0);
    return () => clearTimeout(h);
  }, [q]);
  if (err) return <LoadError error={err} />;
  const month = (m: string | null) => (m ? `${t.months[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}` : t.noMonth);
  const statusOf = (r: ArchiveRow) => (r.cancelled ? t.cancelled : r.kind === 'hub' ? (t.status_[r.status ?? ''] ?? r.status ?? '–') : (r.status ?? '–'));
  return (
    <div class="stack">
      <p class="small muted">{t.intro}</p>
      <input type="search" class="input pf-search" placeholder={t.search} aria-label={t.search} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
      {page ? (
        <p class="small muted" aria-live="polite">
          {q.trim() ? t.hits(page.total, q.trim()) : t.count(page.total)}
        </p>
      ) : null}
      {!page ? (
        <Loading />
      ) : !rows.length ? (
        <div class="empty">
          <strong>{t.none}</strong>
        </div>
      ) : (
        <div class="tbl-wrap" role="region" aria-label={t.project} tabIndex={0}>
          <table class="tbl tbl--rows">
            <thead>
              <tr>
                <th scope="col">{t.project}</th>
                <th scope="col">{t.month}</th>
                <th scope="col">{t.status}</th>
                <th scope="col">{t.source}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = r.kind === 'hub' ? r.id : r.hubProjectId;
                return (
                  <tr key={`${r.kind}-${r.id}`} class={r.kind === 'hub' ? 'row-link' : undefined} onClick={r.kind === 'hub' ? () => navigate(`/projects/${r.id}`) : undefined}>
                    <th scope="row">
                      {r.kind === 'hub' ? (
                        <a class="pl-name" href={`/projects/${r.id}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${r.id}`))}>
                          <span class="pl-project">{r.name}</span>
                          <span class="pl-client">{r.clientName ?? '–'}</span>
                        </a>
                      ) : (
                        <span class="pl-name">
                          <span class="pl-project">{r.name}</span>
                          <span class="pl-client">{r.clientName ?? '–'}</span>
                        </span>
                      )}
                    </th>
                    <td class="num">{month(r.month)}</td>
                    <td>{statusOf(r)}</td>
                    <td>{r.kind === 'hub' ? t.hub : t.monday}</td>
                    <td>
                      <span class="cluster">
                        {r.kind === 'monday' && open ? (
                          <a class="btn btn-sm" href={`/projects/${open}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${open}`))}>
                            {t.opened}
                          </a>
                        ) : r.kind === 'monday' && page.canReopen ? (
                          <button type="button" class="btn btn-sm" title={t.reopenHint(r.name)} onClick={() => p.onReopen(r)}>
                            {t.reopen}
                          </button>
                        ) : null}
                        {r.kind === 'monday' && r.driveUrl ? (
                          <a class="small" href={r.driveUrl} target="_blank" rel="noopener noreferrer">
                            {t.drive}
                          </a>
                        ) : null}
                        {r.kind === 'monday' && r.ziflowUrl ? (
                          <a class="small" href={r.ziflowUrl} target="_blank" rel="noopener noreferrer">
                            {t.ziflow}
                          </a>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {page && rows.length < page.total ? (
        <div>
          <button type="button" class="btn" disabled={busy} onClick={() => load(q, rows.length)}>
            {t.more}
          </button>
        </div>
      ) : null}
    </div>
  );
}
